import type { QueryClient } from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
  type ReactNode,
} from "react";
import { useParams } from "react-router-dom";

import AnchorCellImportDialogs, {
  type AnchorCellImportTarget,
} from "../../HistoryImport/AnchorCellImportDialogs";

import {
  useBatchRecalculateCells,
  useCancelCell,
  useCancelRows,
  useImportAnchorCell,
  useRecalculateCell,
  useRecalculateColumn,
  useUpdateCell,
} from "@/queries/smart-tables";
import type {
  SmartTableRequestImportPayload,
  SmartTableTraceImportPayload,
} from "@/queries/smart-tables";
import { useAuth } from "@/context/auth-context";
import { useSheetDataStoreSafe } from "@/routes/smart-table/sheet-data-store-context";
import { HistoryImportSource } from "@/routes/smart-table/utils/historyImportTypes";
import { displayErrorToast } from "@/utils/toast";

export type { AnchorCellImportTarget };

interface SmartTableCellActionContextValue {
  wid: number;
  workspaceId?: string;
  userToken: string;
  queryClient: QueryClient;
  updateCellMutationRef: MutableRefObject<ReturnType<typeof useUpdateCell>>;
  recalculateCellMutationRef: MutableRefObject<ReturnType<typeof useRecalculateCell>>;
  batchRecalculateCellsMutationRef: MutableRefObject<ReturnType<typeof useBatchRecalculateCells>>;
  recalculateColumnMutationRef: MutableRefObject<ReturnType<typeof useRecalculateColumn>>;
  cancelCellMutationRef: MutableRefObject<ReturnType<typeof useCancelCell>>;
  cancelRowsMutationRef: MutableRefObject<ReturnType<typeof useCancelRows>>;
  openAnchorCellImport: (target: AnchorCellImportTarget) => void;
}

const SmartTableCellActionContext = createContext<SmartTableCellActionContextValue | null>(null);

const getExactOneAnchorImportId = (
  type: HistoryImportSource,
  payload: SmartTableRequestImportPayload | SmartTableTraceImportPayload,
): number | string | null => {
  switch (type) {
    case HistoryImportSource.Request:
      return "request_log_ids" in payload && payload.request_log_ids.length === 1
        ? (payload.request_log_ids[0] ?? null)
        : null;
    case HistoryImportSource.Trace:
      return "trace_ids" in payload && payload.trace_ids.length === 1
        ? (payload.trace_ids[0] ?? null)
        : null;
    default:
      return null;
  }
};

export const SmartTableCellActionProvider = ({
  children,
  tableId,
  sheetId,
}: {
  children: ReactNode;
  tableId?: string;
  sheetId?: string;
}) => {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const wid = Number(workspaceId);
  const userToken = useAuth()?.userToken ?? "";
  const queryClient = useQueryClient();
  const sheetDataStore = useSheetDataStoreSafe();
  const { mutateAsync: importAnchorCell, isPending: isImportingAnchorCell } = useImportAnchorCell();
  const [anchorImportTarget, setAnchorImportTarget] = useState<AnchorCellImportTarget | null>(null);

  const updateCellMutation = useUpdateCell();
  const updateCellMutationRef = useRef(updateCellMutation);
  updateCellMutationRef.current = updateCellMutation;

  const recalculateCellMutation = useRecalculateCell();
  const recalculateCellMutationRef = useRef(recalculateCellMutation);
  recalculateCellMutationRef.current = recalculateCellMutation;

  const batchRecalculateCellsMutation = useBatchRecalculateCells();
  const batchRecalculateCellsMutationRef = useRef(batchRecalculateCellsMutation);
  batchRecalculateCellsMutationRef.current = batchRecalculateCellsMutation;

  const recalculateColumnMutation = useRecalculateColumn();
  const recalculateColumnMutationRef = useRef(recalculateColumnMutation);
  recalculateColumnMutationRef.current = recalculateColumnMutation;

  const cancelCellMutation = useCancelCell();
  const cancelCellMutationRef = useRef(cancelCellMutation);
  cancelCellMutationRef.current = cancelCellMutation;

  const cancelRowsMutation = useCancelRows();
  const cancelRowsMutationRef = useRef(cancelRowsMutation);
  cancelRowsMutationRef.current = cancelRowsMutation;

  const openAnchorCellImport = useCallback((target: AnchorCellImportTarget) => {
    setAnchorImportTarget(target);
  }, []);

  const importIntoCell = useCallback(
    async (payload: SmartTableRequestImportPayload | SmartTableTraceImportPayload) => {
      if (!tableId || !sheetId || !anchorImportTarget) {
        displayErrorToast("Select exactly one item to import into this cell.");
        return false;
      }

      const sourceId = getExactOneAnchorImportId(anchorImportTarget.type, payload);
      if (sourceId === null) {
        displayErrorToast(
          `Select exactly one ${anchorImportTarget.type} to import into this cell.`,
        );
        return false;
      }

      try {
        const result = await importAnchorCell({
          tableId,
          sheetId,
          cellId: anchorImportTarget.cellId,
          payload:
            anchorImportTarget.type === HistoryImportSource.Request
              ? { request_log_id: Number(sourceId) }
              : { trace_id: String(sourceId) },
        });
        result.columns.forEach(column => sheetDataStore?.reconcileUpdatedColumn(column));
        sheetDataStore?.reconcileCell(result.cell);
        sheetDataStore?.clearLocalCacheAndReloadFromServer(sheetDataStore.totalRows);
        return true;
      } catch {
        displayErrorToast(`Failed to import ${anchorImportTarget.type} into cell.`);
        return false;
      }
    },
    [anchorImportTarget, importAnchorCell, sheetDataStore, sheetId, tableId],
  );

  const value = useMemo<SmartTableCellActionContextValue>(
    () => ({
      wid,
      workspaceId,
      userToken,
      queryClient,
      updateCellMutationRef,
      recalculateCellMutationRef,
      batchRecalculateCellsMutationRef,
      recalculateColumnMutationRef,
      cancelCellMutationRef,
      cancelRowsMutationRef,
      openAnchorCellImport,
    }),
    [
      cancelCellMutationRef,
      cancelRowsMutationRef,
      batchRecalculateCellsMutationRef,
      queryClient,
      recalculateCellMutationRef,
      recalculateColumnMutationRef,
      updateCellMutationRef,
      openAnchorCellImport,
      userToken,
      wid,
      workspaceId,
    ],
  );

  const closeAnchorCellImport = (open: boolean) => {
    if (!open) {
      setAnchorImportTarget(null);
    }
  };

  return (
    <SmartTableCellActionContext.Provider value={value}>
      {children}
      <AnchorCellImportDialogs
        target={anchorImportTarget}
        isImporting={isImportingAnchorCell}
        tableId={tableId}
        sheetId={sheetId}
        onOpenChange={closeAnchorCellImport}
        onImport={importIntoCell}
      />
    </SmartTableCellActionContext.Provider>
  );
};

export const useSmartTableCellActionContext = () => useContext(SmartTableCellActionContext);
